import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    // three states: 'loading' = checking, 'yes' = authed, 'no' = anon
    const [status, setStatus] = useState("loading");

    // Monotonic version — bumps on every explicit signIn/signOut. Any in-flight
    // refresh() that started BEFORE the bump must not clobber the newer state.
    const authVersion = useRef(0);

    const refresh = useCallback(async () => {
        const startedAt = authVersion.current;
        try {
            const me = await api.me();
            if (authVersion.current !== startedAt) return me; // stale — discard
            setUser(me);
            setStatus("yes");
            return me;
        } catch {
            if (authVersion.current !== startedAt) return null; // stale — discard
            setUser(null);
            setStatus("no");
            return null;
        }
    }, []);

    useEffect(() => {
        // If we're mid-OAuth callback (hash carries the session_id), let the
        // callback route handle the exchange and skip the mount refresh entirely.
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setStatus("loading");
            return;
        }
        refresh();
    }, [refresh]);

    const signIn = useCallback((u) => {
        authVersion.current += 1;
        setUser(u);
        setStatus(u ? "yes" : "no");
    }, []);

    const signOut = useCallback(async () => {
        authVersion.current += 1;
        // Optimistically clear local state so ProtectedRoute stops rendering
        // protected pages. Server call is best-effort.
        setUser(null);
        setStatus("no");
        try { await api.logout(); } catch { /* ignore */ }
    }, []);

    const value = { user, status, refresh, signIn, signOut };
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}

export function dashboardPathForRole(role) {
    if (role === "admin") return "/admin";
    if (role === "affiliate") return "/affiliate";
    return "/dashboard";
}
